import React, { useState, useEffect } from 'react';
import { Button, Card, Stack, Text, Center, TextInput, PasswordInput, Group, Anchor, Alert } from '@mantine/core';
import { IconMail, IconLock, IconInfoCircle } from '@tabler/icons-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Login = () => {
  const { signInWithPassword, sendPasswordReset, updatePassword, error, isAuthLoading, session, authEvent } = useSupabase();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState('signin'); // signin | reset | reset_set
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const handleSubmit = async () => {
    setSubmitting(true);
    setInfo('');
    try {
      if (mode === 'signin') {
        const { error: e } = await signInWithPassword({ email, password });
        if (!e) setInfo('Signed in successfully. Redirecting...');
      } else if (mode === 'reset') {
        const { error: e } = await sendPasswordReset(email);
        if (!e) setInfo('Password reset email sent. Please check your inbox.');
      } else if (mode === 'reset_set') {
        if (!newPassword || newPassword.length < 6) {
          setInfo('Password must be at least 6 characters.');
          return;
        }
        if (newPassword !== confirmNewPassword) {
          setInfo('Passwords do not match');
          return;
        }
        const { error: e } = await updatePassword(newPassword);
        if (!e) {
          setInfo('Password updated. Redirecting...');
          try {
            if (window && window.history && window.location) {
              const cleanPath = window.location.pathname;
              window.history.replaceState({}, document.title, cleanPath);
            }
          } catch {}
          navigate('/', { replace: true });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // If arrived via recovery link, show password set form
  useEffect(() => {
    const hash = window.location.hash || '';
    const search = location.search || '';
    const isRecovery = hash.includes('type=recovery') || search.includes('type=recovery');
    if (isRecovery || authEvent === 'PASSWORD_RECOVERY') {
      setMode('reset_set');
    }
  }, [authEvent, location.search]);

  // Redirect on active session
  useEffect(() => {
    const hash = window.location.hash || '';
    const search = location.search || '';
    const isRecovery = hash.includes('type=recovery') || search.includes('type=recovery');
    if (session && mode !== 'reset_set' && !isRecovery && authEvent !== 'PASSWORD_RECOVERY') {
      navigate('/', { replace: true });
    }
  }, [session, mode, authEvent, navigate, location.search]);

  return (
    <Center style={{ width: '100%', height: '100vh' }}>
      <Card shadow="sm" padding="lg" withBorder style={{ width: 380 }}>
        <Stack gap="md">
          <Text size="xl" fw={700} ta="center">
            {mode === 'signin' && 'Sign in to EDENA Admin'}
            {mode === 'reset' && 'Reset your password'}
            {mode === 'reset_set' && 'Set a new password'}
          </Text>

          {error && (
            <Alert icon={<IconInfoCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}
          {info && (
            <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
              {info}
            </Alert>
          )}

          {mode !== 'reset_set' && (
            <TextInput
              leftSection={<IconMail size={16} />}
              label="Email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
          )}

          {mode !== 'reset' && mode !== 'reset_set' && (
            <PasswordInput
              leftSection={<IconLock size={16} />}
              label="Password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
          )}


          {mode === 'reset_set' && (
            <>
              <PasswordInput
                leftSection={<IconLock size={16} />}
                label="New Password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.currentTarget.value)}
              />
              <PasswordInput
                leftSection={<IconLock size={16} />}
                label="Confirm New Password"
                placeholder="Re-enter new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.currentTarget.value)}
              />
            </>
          )}

          <Button onClick={handleSubmit} loading={submitting || isAuthLoading}>
            {mode === 'signin' && 'Sign In'}
            {mode === 'reset' && 'Send Reset Email'}
            {mode === 'reset_set' && 'Update Password'}
          </Button>

          <Group justify="space-between">
            {mode !== 'reset' && mode !== 'reset_set' ? (
              <Anchor size="sm" onClick={() => setMode('reset')}>Forgot password?</Anchor>
            ) : mode === 'reset' ? (
              <Anchor size="sm" onClick={() => setMode('signin')}>Back to sign in</Anchor>
            ) : (
              <Anchor size="sm" onClick={() => setMode('signin')}>Back to sign in</Anchor>
            )}
            {null}
          </Group>
        </Stack>
      </Card>
    </Center>
  );
};

export default Login;


